'use client';

import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { TEXT_WRAP } from 'src/theme/ui-tokens';

import { Logo } from 'src/components/logo';

import { PublicShareSignupButtons } from './public-share-signup';
import { BackdropVeil, BackdropBehind, BACKDROP_VEIL_Z, BACKDROP_FRONT_Z, ServiceBackdropShell } from './service-backdrop-shell';

import type { BackdropTint } from './service-backdrop-shell';

/**
 * 공유 페이지(비회원 공개)의 **껍데기 한 벌** — 소재·브랜드·크리에이터 공유가 같은 틀을 쓴다.
 *
 * 서비스 화면(왼쪽 메뉴 · 본문 — 계정 머리는 뺀다)을 그대로 깔고(`background`), **밝은 막**(바탕색 반투명 + 흐림)을 씌운다
 * — 까는 틀·화면 막·메뉴 잠금은 `ServiceBackdropShell` 이 맡는다.
 * 막은 두 겹이다 — ① 화면(뷰포트)에 붙은 막: 왼쪽 메뉴까지 덮고, 화면 맨 위 0.15(흐림 없음) → 화면 바닥 0.6(흐림 약 5px)이라
 *   어디까지 내려도 위쪽은 읽히고 아래쪽은 흐릿하게 비친다. ② 바닥 막: 마지막 화면 높이 구간에서 0.95(흐림 8px)까지 짙어져
 *   거의 바탕색이 되고, 그 위에 밝은 바탕 위 짙은 글자로 가입 안내(로고 · 머리말 · 가입·홈 단추)가 선다.
 * 바닥 막의 기준은 `gate` 가 고른다 — `cover` 화면 바닥 · 안내는 화면 아래 고정(소재 공유 — 위에 상세 다이얼로그 `children`) ·
 * `end` 문서 끝 · 안내도 문서 끝에 선다(허브 공유 — 끝까지 내려야 보인다).
 * 🔑 다크 모드에서는 같은 막이 어두운 바탕색이다(`background.paper` 채널).
 * ⚠️ 깔린 화면과 왼쪽 서비스 메뉴(워크스페이스 고르개 · 계정 칸 포함)는 아무것도 눌리지 않고 키보드 초점도 못 들어간다
 *   (`inert` · `pointer-events: none`) — 비회원이 서비스를 쓰는 길이 아니다. 가입 안내는 그 밖이라 눌린다.
 * @param source 어느 공유 페이지인가 — 가입 단추의 `cta_clicked` 에 실린다
 * @param onReopen 닫힌 상세를 다시 여는 단추 — 비면 단추가 없다
 */
export function PublicShareShell({
  background,
  pitch,
  source,
  reopenLabel,
  onReopen,
  gate = 'cover',
  children,
}: {
  background: ReactNode;
  pitch: { title: string; body: string };
  source: string;
  reopenLabel?: string;
  onReopen?: () => void;
  /** 막의 기준 — `cover` 화면 높이 · 단추 고정(기본 · 소재 공유) · `end` 문서 높이 · 단추는 문서 끝(허브 공유) */
  gate?: 'cover' | 'end';
  children?: ReactNode;
}) {
  const band = (
    <GateBand pitch={pitch} source={source} reopenLabel={reopenLabel} onReopen={onReopen} />
  );
  return (
    <ServiceBackdropShell tint={SCREEN_TINT} blurMask={SCREEN_BLUR_MASK}>
      {gate === 'end' ? (
        <Box sx={{ position: 'relative', width: '100%' }}>
          <BackdropBehind pb={0}>{background}</BackdropBehind>
          {/* ② 문서 끝의 바닥 막 — 마지막 화면 높이 구간에서 짙어진다 */}
          <BackdropVeil data-share-veil="end" position="absolute" zIndex={BACKDROP_VEIL_Z} tint={FLOOR_TINT} blurMask={FLOOR_BLUR_MASK} />
          <Box data-share-gate="end" sx={{ position: 'absolute', zIndex: BACKDROP_FRONT_Z, left: 0, right: 0, bottom: 0, pb: BAND_PB }}>{band}</Box>
        </Box>
      ) : (
        <>
          <BackdropBehind pb={24}>{background}</BackdropBehind>
          {/* ② 화면 바닥의 바닥 막 */}
          <BackdropVeil data-share-gate="cover" position="fixed" zIndex={BACKDROP_VEIL_Z} tint={FLOOR_TINT} blurMask={FLOOR_BLUR_MASK} />
          <Box sx={{ position: 'fixed', zIndex: BACKDROP_FRONT_Z, left: 0, right: 0, bottom: 0, pb: BAND_PB }}>{band}</Box>
        </>
      )}

      {children}
    </ServiceBackdropShell>
  );
}

// ---- 이하 비공개 ----------------------------------------------------------

/** 가장 짙은 바닥의 높이 — 가입 안내가 그 위에 선다(폰에서 단추가 두 줄로 접혀도 들어간다) */
const BAND_ZONE = 320;
const BAND_PB = 'calc(env(safe-area-inset-bottom, 0px) + 40px)';

/** ① 화면 막 — 맨 위 0.15 → 가운데 0.3 → 바닥 0.6 · 흐림은 위 3분의 1 동안 없다가 바닥에서 0.6(약 5px) */
const SCREEN_TINT: BackdropTint = (a) => `linear-gradient(to bottom, ${a(0.15)} 0, ${a(0.3)} 50%, ${a(0.6)} 100%)`;
const SCREEN_BLUR_MASK = 'linear-gradient(to bottom, transparent 0, transparent 35%, rgba(0,0,0,0.6) 100%)';

/** ② 바닥 막 — 마지막 화면 높이 시작점까지 투명 → 바닥 구간 0.95 · 흐림 8px 전부 */
const FLOOR_TINT: BackdropTint = (a) => `linear-gradient(to bottom, ${a(0)} 0, ${a(0)} calc(100% - 100vh), ${a(0.95)} calc(100% - ${BAND_ZONE}px), ${a(0.95)} 100%)`;
const FLOOR_BLUR_MASK = `linear-gradient(to bottom, transparent 0, transparent calc(100% - 100vh), #000 calc(100% - ${BAND_ZONE}px))`;

/** 가입 안내 — 로고(누를 수 없다) · 머리말 · 가입·홈 단추 · (닫힌 상세가 있으면) 다시 보기 · 밝은 바탕 위 짙은 글자 */
function GateBand({ pitch, source, reopenLabel, onReopen }: { pitch: { title: string; body: string }; source: string; reopenLabel?: string; onReopen?: () => void }) {
  return (
    <Stack alignItems="center" spacing={1.5} sx={{ position: 'relative', px: 2, textAlign: 'center', color: 'text.primary' }}>
      {/* 로고는 그림일 뿐이다 — 홈 링크로 눌리거나 초점을 받지 않는다(눌리는 것은 가입·홈 단추뿐) */}
      <Box inert sx={{ display: 'flex' }}>
        <Logo isSingle disabled sx={{ width: 40, height: 40 }} />
      </Box>
      <Typography variant="h5" sx={{ fontWeight: 600, ...TEXT_WRAP.title }}>
        {pitch.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560, ...TEXT_WRAP.body }}>
        {pitch.body}
      </Typography>
      <PublicShareSignupButtons tone="light" source={source} />
      {onReopen && reopenLabel && (
        <Button size="small" color="inherit" onClick={onReopen} sx={{ color: 'text.secondary' }}>
          {reopenLabel}
        </Button>
      )}
    </Stack>
  );
}
